// src/contexts/OrganizationContext.js - Supabase Auth対応版
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabase';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

// ✅ 新規追加：安全な organizationId 取得 Hook
/**
 * 安全に organizationId を取得する Hook
 * @returns {string} organizationId
 * @throws {Error} 組織情報が取得できない場合
 */
export const useOrganizationId = () => {
  const { currentOrganization, isLoading, error } = useOrganization();
  
  if (isLoading) {
    throw new Error('Organization data is still loading. Please wait.');
  }
  
  if (error) {
    throw new Error(`Organization error: ${error}`);
  }
  
  if (!currentOrganization?.id) {
    throw new Error('Organization ID not found. Please log in again.');
  }
  
  return currentOrganization.id;
};

export const OrganizationProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // 病院階層
  const [currentHospital, setCurrentHospital] = useState(null);     // { id, name } | null
  const [isHospitalAdmin, setIsHospitalAdmin] = useState(false);
  const [siblingOrganizations, setSiblingOrganizations] = useState([]);  // 同じ病院配下の全部署

  console.log('🔄 OrganizationProvider 初期化開始（Supabase Auth版）');

  useEffect(() => {
    // 初期セッションを取得
    const loadInitialSession = async () => {
      try {
        setIsLoading(true);
        console.log('📋 Supabase Authセッション取得中...');
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          console.log('✅ Supabaseセッション発見:', session.user.email);
          await loadUserData(session.user);
        } else {
          // セッションがない場合はlocalStorageからフォールバック
          console.log('⚠️ Supabaseセッションなし - localStorageから読み込み');
          loadFromLocalStorage();
        }
      } catch (err) {
        console.error('❌ セッション取得エラー:', err);
        loadFromLocalStorage();
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialSession();

    // Supabase Authの状態変化を監視
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      console.log('🔄 Supabase Auth状態変化:', _event);
      
      if (session?.user) {
        console.log('👤 ログイン検出:', session.user.email);
        await loadUserData(session.user);
      } else {
        console.log('👤 ログアウト検出');
        setCurrentUser(null);
        setCurrentOrganization(null);
        setUserOrganizations([]);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Supabaseユーザーからデータを読み込む
  const loadUserData = async (user) => {
    try {
      console.log('📋 ユーザーデータ読み込み:', user.email);

      // 🔥 自社スタッフ (Internal) が組織選択中なら、その組織の admin として動作させる
      //   - supabase_user を上書きして「該当組織の admin」として偽装
      //   - 既存ページは useOrganization() の currentOrganization をそのまま使えるので
      //     何も変更なしで通常の admin 体験になる
      //
      // 🛡 セキュリティ: 以前の bug で、localStorage.internal_selected_org だけを根拠に Internal モード
      //    扱いしていたため、別アカウントでログインしても stale な internal_selected_org が残っていると
      //    その組織の UI を描画してしまい、テナント越境漏洩が起きていた。
      //    必ず「現在の auth user が本物の internal_staff.is_active=true であること」を DB 検証する。
      // 🛡 Internal モード判定:
      //    DB 検証は本番で RLS 評価が重く hang するケースがあるため廃止。
      //    代わりに「supabase_user.name が '🔧 Internal' で始まる」= selectOrganization 由来、
      //    というマーカーで判断する。 ログイン/ログアウト時に internal_selected_org は
      //    クリアされるので、別アカウントログインで stale な localStorage を使う事故は起きない。
      const internalOrgRaw = localStorage.getItem('internal_selected_org');
      let isMarkedAsInternalBySelectOrg = false;
      try {
        const supabaseUserRaw = localStorage.getItem('supabase_user');
        if (supabaseUserRaw) {
          const parsed = JSON.parse(supabaseUserRaw);
          isMarkedAsInternalBySelectOrg = typeof parsed?.name === 'string'
            && parsed.name.startsWith('🔧 Internal');
        }
      } catch (_) {}
      if (internalOrgRaw && !isMarkedAsInternalBySelectOrg) {
        console.warn('🛡 internal_selected_org 残骸をクリア (supabase_user が Internal 由来でないため)');
        localStorage.removeItem('internal_selected_org');
        localStorage.removeItem('internal_edit_session');
      }
      if (isMarkedAsInternalBySelectOrg && internalOrgRaw) {
        try {
          const org = JSON.parse(internalOrgRaw);
          if (org?.id) {
            console.log('🔧 Internal モード検出: 組織として動作 →', org.name || org.id);
            setCurrentUser({
              id: user.id,
              email: user.email,
              user_metadata: {
                employee_id: 'internal',
                full_name: `🔧 Internal (${org.name || org.id})`,
                role: 'admin',
              },
            });
            const organization = {
              id: org.id,
              name: org.name || org.id,
              type: 'hospital',
            };
            setCurrentOrganization(organization);
            setUserOrganizations([organization]);
            setError(null);
            // Header / 既存コード互換のため supabase_user も書き換え
            localStorage.setItem('supabase_user', JSON.stringify({
              name: `🔧 Internal (${org.name || org.id})`,
              role: 'admin',
              organizationId: org.id,
              organizationName: org.name || org.id,
              organizationType: 'hospital',
            }));
            return;
          }
        } catch (_) { /* JSON 不正 → 通常フロー */ }
      }

      // user_metadataから情報を取得
      // role は user_metadata.role を優先、なければ app_metadata.staff_role（招待経由作成ユーザー用）
      const employeeId = user.user_metadata?.employee_id;
      const fullName = user.user_metadata?.full_name;
      const role = user.user_metadata?.role
        || user.app_metadata?.staff_role
        || 'user';
      
      // localStorageから詳細情報を取得
      const supabaseUser = localStorage.getItem('supabase_user');
      let userData = null;
      
      if (supabaseUser) {
        userData = JSON.parse(supabaseUser);
        console.log('✅ localStorage追加情報:', userData);
      }
      
      // currentUserを設定
      setCurrentUser({
        id: user.id,
        email: user.email,
        user_metadata: {
          employee_id: employeeId,
          full_name: fullName,
          role: role
        }
      });
      
      // 組織情報を取得
      let organizationId, organizationName, organizationType;
      
      if (userData) {
        // localStorageから取得
        organizationId = userData.organizationId;
        organizationName = userData.organizationName;
        organizationType = userData.organizationType;
      }
      
      // 組織情報が取得できない場合はlocalStorageから直接読む
      if (!organizationId) {
        const orgData = localStorage.getItem('currentOrganization');
        if (orgData) {
          const org = JSON.parse(orgData);
          organizationId = org.id;
          organizationName = org.name;
          organizationType = org.type;
        }
      }
      
      if (organizationId) {
        const organization = {
          id: organizationId,
          name: organizationName || '組織名未設定',
          type: organizationType || 'hospital'
        };
        
        console.log('✅ 組織情報設定:', organization);
        setCurrentOrganization(organization);
        setUserOrganizations([organization]);
        setError(null);
      } else {
        console.warn('⚠️ 組織情報が見つかりません');
        setError('組織情報の取得に失敗しました。再ログインしてください。');
      }
      
    } catch (err) {
      console.error('❌ ユーザーデータ読み込みエラー:', err);
      setError('ユーザー情報の読み込みに失敗しました');
    }
  };

  // localStorageからフォールバック読み込み
  const loadFromLocalStorage = () => {
    try {
      console.log('📋 localStorageからフォールバック読み込み');
      
      const supabaseUser = localStorage.getItem('supabase_user');
      
      if (supabaseUser) {
        const userData = JSON.parse(supabaseUser);
        console.log('✅ localStorage ユーザー情報:', userData);
        
        setCurrentUser({
          id: userData.auth_user_id || userData.authUserId,
          email: userData.email,
          user_metadata: {
            employee_id: userData.employeeId || userData.employee_id,
            full_name: userData.name,
            role: userData.role
          }
        });
        
        const organizationId = userData.organizationId;
        
        if (organizationId) {
          const organization = {
            id: organizationId,
            name: userData.organizationName || '組織名未設定',
            type: userData.organizationType || 'hospital'
          };
          
          console.log('✅ localStorage 組織情報:', organization);
          setCurrentOrganization(organization);
          setUserOrganizations([organization]);
          setError(null);
        } else {
          console.warn('⚠️ localStorage: 組織IDなし');
          setError('組織情報の取得に失敗しました。再ログインしてください。');
        }
      } else {
        console.warn('⚠️ localStorageにユーザー情報なし');
        setCurrentUser(null);
        setCurrentOrganization(null);
        setUserOrganizations([]);
      }
    } catch (err) {
      console.error('❌ localStorage読み込みエラー:', err);
      setError('ユーザー情報の読み込みに失敗しました');
    }
  };

  // currentOrganization が確定したら、その org の hospital_id と、同じ病院配下の部署、
  // 自分が hospital_admin かを一括で取得する。
  useEffect(() => {
    const loadHospitalContext = async () => {
      if (!currentOrganization?.id || !currentUser?.id) {
        setCurrentHospital(null);
        setIsHospitalAdmin(false);
        setSiblingOrganizations([]);
        return;
      }
      try {
        // 1) 現在の org の hospital_id を取得
        const { data: orgRow, error: orgErr } = await supabase
          .from('organizations')
          .select('id, name, type, hospital_id')
          .eq('id', currentOrganization.id)
          .maybeSingle();
        if (orgErr) {
          console.warn('🏥 organizations SELECT 失敗:', orgErr.message);
          throw orgErr;
        }
        const hospitalId = orgRow?.hospital_id;
        console.log('🏥 loadHospitalContext: orgRow=', orgRow, 'hospitalId=', hospitalId);
        if (!hospitalId) {
          console.warn('🏥 organizations.hospital_id が未設定。マイグレーションが未適用の可能性');
          setCurrentHospital(null);
          setIsHospitalAdmin(false);
          setSiblingOrganizations([currentOrganization]);
          return;
        }

        // 2) hospital 情報を取得
        const { data: hospitalRow, error: hospitalErr } = await supabase
          .from('hospitals')
          .select('id, name')
          .eq('id', hospitalId)
          .maybeSingle();
        if (hospitalErr) {
          console.warn('🏥 hospitals SELECT 失敗 (RLS?):', hospitalErr.message);
        }
        console.log('🏥 hospitalRow=', hospitalRow);
        setCurrentHospital(hospitalRow || { id: hospitalId, name: '' });

        // 3) 自分が hospital_admin か判定
        const { data: adminRow } = await supabase
          .from('hospital_admins')
          .select('hospital_id, role')
          .eq('hospital_id', hospitalId)
          .eq('user_id', currentUser.id)
          .maybeSingle();
        const isAdmin = !!adminRow;
        setIsHospitalAdmin(isAdmin);

        // 4) 同じ病院配下の全部署
        //    hospital_admin なら RLS 無しで全部署見える想定 (= 現状 RLS は組織メンバー基準で
        //    siblings が見えないため、関数経由で取得する実装は後日検討。 今は .select で試みる)。
        const { data: siblings, error: sibErr } = await supabase
          .from('organizations')
          .select('id, name, type, hospital_id')
          .eq('hospital_id', hospitalId)
          .order('name', { ascending: true });
        if (sibErr) {
          console.warn('sibling organizations 取得失敗:', sibErr.message);
          setSiblingOrganizations([currentOrganization]);
        } else {
          setSiblingOrganizations(siblings || [currentOrganization]);
        }
      } catch (e) {
        console.warn('loadHospitalContext 失敗:', e.message);
        setCurrentHospital(null);
        setIsHospitalAdmin(false);
        setSiblingOrganizations([currentOrganization]);
      }
    };
    loadHospitalContext();
  }, [currentOrganization?.id, currentUser?.id]);

  // 部署切替: 兄弟 org に切り替える。 localStorage を書き換えてから state を更新。
  const switchOrganization = (targetOrgId) => {
    if (!targetOrgId) return;
    const target = siblingOrganizations.find((o) => o.id === targetOrgId);
    if (!target) {
      console.warn('switchOrganization: 対象部署が siblings に見つからない', targetOrgId);
      return;
    }
    const nextOrg = {
      id: target.id,
      name: target.name,
      type: target.type || 'hospital',
    };
    try {
      const raw = localStorage.getItem('supabase_user');
      if (raw) {
        const u = JSON.parse(raw);
        u.organizationId = nextOrg.id;
        u.organizationName = nextOrg.name;
        u.organizationType = nextOrg.type;
        localStorage.setItem('supabase_user', JSON.stringify(u));
      }
      localStorage.setItem('currentOrganization', JSON.stringify(nextOrg));
    } catch (_) { /* ignore */ }
    setCurrentOrganization(nextOrg);
    setUserOrganizations([nextOrg]);
  };

  // ※ 部署追加は運営パネル (OperatorDashboard) 経由のみ。
  //   ユーザー側 (hospital_admin) 自身が追加できないよう、ここには実装を置かない。

  const hasPermission = (permission) => {
    if (!currentUser) return false;

    const userRole = currentUser.user_metadata?.role || 'user';
    if (userRole === 'admin') return true;

    const permissionMap = {
      'schedule:read': ['admin', 'user'],
      'schedule:write': ['admin'],
      'org:write': ['admin']
    };

    return permissionMap[permission]?.includes(userRole) || false;
  };

  // デバッグログ
  useEffect(() => {
    console.log('📊 OrganizationContext状態:', {
      currentUser: currentUser?.email,
      currentOrganization: currentOrganization?.id,
      userOrganizations: userOrganizations?.length,
      isLoading,
      error
    });
  }, [currentUser, currentOrganization, userOrganizations, isLoading, error]);

  const value = {
    currentUser,
    currentOrganization,
    userOrganizations,
    isLoading,
    error,
    hasPermission,
    // 病院階層
    currentHospital,
    isHospitalAdmin,
    siblingOrganizations,
    switchOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};